import { fromPairs } from 'lodash';
import { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';

export type QueryKeyFn = (params?: any) => readonly [string, ...any[]];

export interface IUseQueryDisplayCacheInput<T extends object> {
  keyFn: QueryKeyFn;
  id: keyof T;
  initialData?: T[];
}

function isSameFirstQueryKey(key1: readonly string[], key2: readonly string[]) {
  return key1?.[0] === key2?.[0];
}

function getCacheableItems(data: unknown) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (
    typeof data === 'object' &&
    data != null &&
    'dati' in data &&
    Array.isArray(data.dati)
  ) {
    return data.dati;
  }
  console.warn(`[useQueryDisplayCache] Unexpected data: it's not an Array or a Paginated result`, data); // prettier-ignore
  return [];
}

export function useQueryDisplayCache<T extends object>(
  input: IUseQueryDisplayCacheInput<T>,
) {
  const queryClient = useQueryClient();

  // TODO Inserire una WeakMap per evitare memory leak
  const [displayCache, setDisplayCache] = useState<Record<string, T>>(
    input.initialData
      ? fromPairs(input.initialData.map(item => [item[input.id], item]))
      : {},
  );

  useEffect(() => {
    if (!input.initialData) return;
    const items = getCacheableItems(input.initialData) as T[];
    setDisplayCache(cacheItems => ({
      ...cacheItems,
      ...fromPairs(items.map(item => [item[input.id], item])),
    }));
  }, [input.id, input.initialData]);

  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe(event => {
      if (
        event?.type === 'queryUpdated' &&
        isSameFirstQueryKey(event.query.queryKey, input.keyFn())
      ) {
        const items = getCacheableItems(event.query.state.data) as T[];
        setDisplayCache(cacheItems => ({
          ...cacheItems,
          ...fromPairs(items.map(item => [item[input.id], item])),
        }));
      }
    });

    return () => unsubscribe();
  }, [input, queryClient]);

  console.log('displayCache', displayCache);

  return displayCache;
}
